import { useEffect, useState } from "react";
import { BellRing, BellOff, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { enablePush, disablePush, getPushState, isStandaloneIOS, resyncPush } from "@/lib/push";

export default function PushToggle() {
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => { getPushState().then(setState); resyncPush(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (state === "on") { await disablePush(); setState("off"); toast("Push alerts turned off"); }
      else {
        await enablePush(); setState("on");
        toast.success("Push alerts on — you'll get boot alerts even when the app is closed");
        api.post("/push/test").catch(() => {});
      }
    } catch (e) {
      const st = await getPushState(); setState(st);
      toast.error(e?.message || "Could not enable push");
    } finally { setBusy(false); }
  };

  if (state === "loading") return null;
  if (state === "unsupported") {
    return (
      <div className="px-3 py-2 text-xs text-[#94A3B8] flex items-start gap-2" data-testid="push-unsupported">
        <Smartphone className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{isStandaloneIOS() ? "On iPhone: tap Share → “Add to Home Screen”, then open ParkLock from there to enable push alerts." : "Push alerts aren't supported in this browser."}</span>
      </div>
    );
  }
  if (state === "denied") {
    return (
      <div className="px-3 py-2 text-xs text-amber-300 flex items-center gap-2" data-testid="push-denied">
        <BellOff className="w-4 h-4" /> Notifications blocked — allow them in your browser settings.
      </div>
    );
  }
  return (
    <button type="button" onClick={toggle} disabled={busy}
      className="w-full px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-[#1C263B] rounded-sm disabled:opacity-60"
      data-testid="push-toggle" data-state={state}>
      <span className="flex items-center gap-2">
        {state === "on" ? <BellRing className="w-4 h-4 text-emerald-400" /> : <BellOff className="w-4 h-4 text-[#94A3B8]" />}
        Phone push alerts
      </span>
      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${state === "on" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-[#28364F] text-[#94A3B8]"}`}>
        {busy ? "…" : state === "on" ? "On" : "Off"}
      </span>
    </button>
  );
}
